'use client';

import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { BaseLineClear } from '@/app/components/icons/BaseLineClear';
import { BaseLineSearch } from '@/app/components/icons/BaseLineSearch';
import { ChevronDown } from '@/app/components/icons/ChevronDown';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/app/components/ui/dropdown-menu';
import { Input } from '@/app/components/ui/input';
import { cn } from '@/app/utils';
import type { FilterOption } from './types';

interface FilterDropdownProps<T extends string> {
  label: string;
  options: FilterOption[];
  searchable?: boolean;
  searchPlaceholder?: string;
  selectedValues: T[];
  onSelectionChange: (values: T[]) => void;
}

export const FilterDropdown = <T extends string>({
  label,
  options,
  searchable = false,
  searchPlaceholder,
  selectedValues,
  onSelectionChange,
}: FilterDropdownProps<T>) => {
  const t = useTranslations();
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!searchable || !normalizedQuery) {
      return options;
    }

    return options.filter((option) => (option.searchLabel ?? option.label).toLowerCase().includes(normalizedQuery));
  }, [options, query, searchable]);

  const handleMultiSelect = (value: T) => {
    if (value === 'All') {
      onSelectionChange([]);
      return;
    }

    const newValues = selectedValues.includes(value)
      ? selectedValues.filter((v) => v !== value)
      : [...selectedValues, value];
    onSelectionChange(newValues);
  };

  const getButtonLabel = () => {
    if (selectedValues.length === 0) return label;
    return selectedValues.map((v) => options.find((o) => o.key === v)?.label).join(', ');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          className={cn(
            'flex h-8 max-w-[128px] items-center gap-2 rounded-8 bg-system-white10 px-3 font-normal text-sm aria-expanded:bg-system-white24',
            selectedValues.length > 0 && 'bg-system-white24',
          )}
        >
          <CustomTooltip content={getButtonLabel()} showOnlyOnOverflow maxLines={1}>
            <span>{getButtonLabel()}</span>
          </CustomTooltip>
          <ChevronDown className="flex-shrink-0 text-base" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[calc(100vh-300px)] space-y-1 overflow-y-auto border border-system-white12 bg-[#1A1A1A]"
      >
        {searchable && (
          <div className="relative pb-1">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder || t('creations.search.placeholder')}
              className={cn(
                'peer h-8 w-full min-w-0 rounded-8 border-none bg-system-white10 ps-8 text-sm placeholder:text-system-white36 focus-visible:bg-[#232933] focus-visible:text-system-white',
                query ? 'pe-8' : 'pe-4',
                'focus:border-system-white10',
              )}
              onKeyDown={(e) => e.stopPropagation()}
            />
            <BaseLineSearch className="-translate-y-1/2 pointer-events-none absolute top-1/2 left-4 text-base text-system-white opacity-30 peer-focus-visible:text-system-white" />
            {query && (
              <BaseLineClear
                className="-translate-y-1/2 absolute top-1/2 right-4 cursor-pointer text-base text-system-white opacity-80 transition-opacity hover:opacity-90"
                onClick={() => setQuery('')}
              />
            )}
          </div>
        )}
        <DropdownMenuCheckboxItem
          key="All"
          checked={selectedValues.length === 0}
          onSelect={(e) => e.preventDefault()}
          onClick={() => handleMultiSelect('All' as T)}
        >
          {t('creations.filters.all')}
        </DropdownMenuCheckboxItem>
        {filteredOptions.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.key}
            checked={selectedValues.includes(option.key as T)}
            onSelect={(e) => e.preventDefault()}
            onClick={() => handleMultiSelect(option.key as T)}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
